import React, { ComponentProps, useRef, useState } from 'react';
import { Box, Flex } from '@qga/roo-ui/components';

// Types
import { BlackoutDates } from '@shared/types/client/BlackoutDates';
// Shared libs
import Dropdown from '@shared/v2/components/Dropdown';
import TravelDatesCalendar from '@shared/v2/components/TravelDatesCalendar';
import usePackageSearch, {
  ValidationError,
} from '@shared/v2/hooks/usePackageSearch';
import { getProcuredOfferCalendarDatesMessage } from '@shared/v2/utils/procuredOffer';

import CampaignHeader from '@/components/DateRange/components/CampaignHeader';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
// Hooks
import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
import useDestination from '@/hooks/useDestination';
import usePropertyContent from '@/hooks/usePropertyContent';
import { longDate } from '@/lib/date/format';
import Input, { InputProps } from '@/v2/components/ui/Input/Input';
import TermsAndConditions from '@/v2/components/ui/TermsAndConditions';
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import { RoomType } from '@/v2/services/types/api/parsedResponse';
import { UI } from '@/v2/types';
import { useCampaignMessage } from './hooks';

export interface Dates {
  departureDate?: string | Date;
  returnDate?: string | Date;
}

interface DateRangeInputProps extends Pick<InputProps, 'disabled'> {
  dates: Dates;
  blackoutDates: BlackoutDates;
  minimumNights: number;
  campaign?: UI.Campaign | null;
  error?: ValidationError;
  onDataChange: (dates?: Dates) => void;
  onDataSubmit: (selectedRoomId?: string) => void;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  roomId?: string;
  roomTypes?: ComponentProps<typeof TravelDatesCalendar>['roomTypes'];
}

const DateRangeInput = ({
  dates,
  blackoutDates,
  minimumNights,
  campaign,
  error,
  disabled,
  onDataChange,
  onDataSubmit,
  isOpen,
  onClose,
  onOpen,
  roomId,
  roomTypes: roomTypesProp,
}: DateRangeInputProps) => {
  const { departureDate, returnDate } = dates;

  const startDateRef = useRef(departureDate);
  const [startDate, setStartDate] = useState(startDateRef.current);

  const breakpoints = useBreakpoints();
  const smallScreen = isScreen(breakpoints)('xs', 'sm');
  const dropDownPosition = smallScreen ? 'BOTTOM_WITH_OFFSET' : 'BOTTOM_RIGHT';

  const propertyContent = usePropertyContent() || {};
  const procuredOffer = useProcuredOffer();

  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;

  const destinationCode = destination.searchConfig.destinationCode;
  const {
    data: { originCode },
  } = usePackageSearch();
  const campaignMessage = useCampaignMessage({
    campaign,
    procuredOffer,
    originCode,
    destinationCode,
  });
  const procuredOfferMessage =
    getProcuredOfferCalendarDatesMessage(procuredOffer);

  const { id: propertyId, roomTypes: allRoomTypes } = propertyContent;

  const roomTypes =
    roomTypesProp ??
    allRoomTypes?.map((room: RoomType) => ({
      value: room.id,
      text: room.name,
    }));

  return (
    <Box data-testid="DATE_RANGE_INPUT_WRAPPER" id="date-range">
      <Dropdown
        isOpen={isOpen}
        position={dropDownPosition}
        fullscreenOnMobile
        toggle={(ref) => {
          const openWithStartDate = () => {
            setStartDate(startDateRef.current);
            onOpen();
          };

          return (
            <Flex ref={ref}>
              <Box width="100%">
                <Input
                  label="When"
                  placeholder="Select travel dates"
                  icon="calendar"
                  height="68px"
                  dataTestId="DATE_RANGE_INPUT"
                  onClick={openWithStartDate}
                  value={
                    returnDate
                      ? `${longDate(departureDate)} - ${longDate(returnDate)}`
                      : undefined
                  }
                  error={error && (error.departureDate || error.returnDate)}
                  disabled={disabled}
                />
              </Box>
            </Flex>
          );
        }}
        content={
          <TravelDatesCalendar
            blackoutDates={blackoutDates}
            brand="jetstar"
            campaignHeader={
              campaignMessage && (
                <CampaignHeader title={campaignMessage.title} />
              )
            }
            campaignMessage={
              campaignMessage?.description ?? procuredOfferMessage
            }
            date={startDate!}
            departureDate={departureDate}
            minimumNights={minimumNights}
            onChange={(dates: Dates) => {
              startDateRef.current = dates.departureDate;
              onDataChange(dates);
            }}
            onClearDates={() => {
              onDataChange();
            }}
            onSubmit={(selectedRoomId) => {
              onDataChange(dates);
              onDataSubmit(selectedRoomId);
              onClose();
            }}
            propertyId={propertyId}
            returnDate={returnDate}
            roomTypes={roomTypes}
            submitText="Confirm"
            tandcs={
              <TermsAndConditions
                campaign={campaign}
                propertyContent={propertyContent}
                procuredOffer={procuredOffer}
              />
            }
            roomId={roomId}
          />
        }
      />
    </Box>
  );
};

export default DateRangeInput;
